import { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, User, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Player } from '../../types';

interface SearchablePlayerSelectProps {
  value: string;
  players: Player[];
  onChange: (value: string) => void;
  placeholder: string;
  className?: string;
  disabled?: boolean;
}

export function SearchablePlayerSelect({ value, players, onChange, placeholder, className = "", disabled }: SearchablePlayerSelectProps) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const selectedPlayer = players.find((p) => p.id === value);
  const selectedName = selectedPlayer ? `${selectedPlayer.name}` : '';

  useEffect(() => {
    if (!isOpen) setSearch(selectedName);
  }, [isOpen, selectedName]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = players.filter((p) => p.name.toLowerCase().includes(search.toLowerCase())).slice(0, 3);

  return (
    <div className={`relative w-full ${isOpen ? 'z-[100]' : 'z-0'}`} ref={wrapperRef}>
      <div className="relative group/select">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 opacity-20 group-focus-within/select:opacity-100 group-focus-within/select:theme-primary-text transition-all">
          <User size={14} />
        </div>
        <input
          type="text"
          value={isOpen ? search : selectedName}
          disabled={disabled}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (disabled) return;
            setIsOpen(true);
            setSearch('');
          }}
          placeholder={placeholder}
          className={`w-full bg-white/[0.03] border border-white/10 rounded-2xl py-4 pl-10 pr-10 font-bold text-[11px] uppercase outline-none focus:theme-primary-border focus:bg-white/[0.06] transition-all truncate placeholder:opacity-20 ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 pointer-events-none">
          {isOpen ? <Search size={14} className="opacity-40" /> : <ChevronDown size={14} className={`opacity-20 transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute z-[9999] w-full mt-2 max-h-[300px] overflow-hidden bg-slate-900/95 backdrop-blur-2xl border border-white/10 rounded-[24px] shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex flex-col"
          >
            <div className="overflow-y-auto custom-scrollbar">
              {filtered.map((p) => (
                <div
                  key={p.id}
                  className="px-4 py-3.5 flex items-center justify-between group/item hover:bg-white/[0.05] transition-all cursor-pointer border-b border-white/5 last:border-0"
                  onClick={() => {
                    onChange(p.id);
                    setIsOpen(false);
                    setSearch(p.name);
                  }}
                >
                  <div className="flex flex-col gap-0.5 truncate">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-black uppercase text-white group-hover/item:theme-primary-text transition-colors truncate tracking-tight">
                        {p.name}
                      </span>
                      {p.phone && <Phone size={10} className="text-white opacity-80 shrink-0" />}
                    </div>
                    <span className="text-[9px] font-bold text-white/30 group-hover/item:text-white/60 transition-colors uppercase">
                      Categoría: {p.category}
                    </span>
                  </div>
                  <div className="opacity-0 group-hover/item:opacity-100 transition-opacity">
                    <ChevronDown size={14} className="-rotate-90 theme-primary-text" />
                  </div>
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="px-5 py-8 text-center">
                  <p className="text-[10px] font-black uppercase opacity-20 tracking-widest italic">
                    No se encontraron jugadores
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
